import { useEffect, useState } from 'react';
import { ChevronRight, ShieldCheck } from 'lucide-react';
import type { OtpChallenge } from '../../types/user';
import { OTP_LENGTH, otpTimeLeft } from '../../core/otp';
import { maskPhone } from '../../core/phone';
import { AppSignature, AuthShell } from './AuthShell';
import { CodeInput } from './CodeInput';

interface OtpVerifyStepProps {
  challenge: OtpChallenge;
  onVerify: (code: string) => void;
  onResend: () => void;
  onBack: () => void;
  busy?: boolean;
  error?: string | null;
}

function formatCountdown(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** Step 2 — six digit OTP verification (delivery is simulated). */
export function OtpVerifyStep({
  challenge,
  onVerify,
  onResend,
  onBack,
  busy = false,
  error = null,
}: OtpVerifyStepProps) {
  const [code, setCode] = useState('');
  const [timeLeft, setTimeLeft] = useState(() => otpTimeLeft(challenge));

  useEffect(() => {
    setCode('');
    setTimeLeft(otpTimeLeft(challenge));
    const timer = window.setInterval(() => setTimeLeft(otpTimeLeft(challenge)), 1000);
    return () => window.clearInterval(timer);
  }, [challenge]);

  const expired = timeLeft <= 0;

  return (
    <AuthShell
      icon={<ShieldCheck size={22} aria-hidden />}
      title="אימות קוד"
      subtitle={
        <>
          שלחנו קוד בן {OTP_LENGTH} ספרות אל <span data-dir="ltr">{maskPhone(challenge.phone)}</span>
        </>
      }
      footer={<AppSignature />}
    >
      <div className="mb-5 rounded-xl bg-brand-50 border border-brand-100 p-3 text-center">
        <p className="text-xs text-brand-700 mb-1">סימולציית SMS — הקוד שלכם:</p>
        <p className="text-2xl font-bold tracking-[0.35em] text-brand-900" data-dir="ltr">
          {challenge.code}
        </p>
      </div>

      <CodeInput
        length={OTP_LENGTH}
        value={code}
        onChange={setCode}
        onComplete={onVerify}
        label="קוד אימות"
        disabled={busy || expired}
        invalid={Boolean(error)}
      />

      {error ? <p className="mt-3 text-sm text-red-600 text-center">{error}</p> : null}

      <p className="mt-3 text-sm text-center text-ink/60">
        {expired ? (
          'הקוד פג תוקף'
        ) : (
          <>
            הקוד תקף עוד <span data-dir="ltr">{formatCountdown(timeLeft)}</span> · נותרו{' '}
            {challenge.attemptsLeft} ניסיונות
          </>
        )}
      </p>

      <button
        type="button"
        onClick={() => onVerify(code)}
        disabled={busy || expired || code.length !== OTP_LENGTH}
        className="mt-5 w-full h-12 rounded-xl bg-brand-500 text-white font-semibold transition-colors hover:bg-brand-700 disabled:opacity-40 disabled:hover:bg-brand-500"
      >
        {busy ? 'מאמת…' : 'אישור'}
      </button>

      <div className="mt-4 flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 text-ink/60 hover:text-ink"
        >
          <ChevronRight size={16} aria-hidden />
          מספר אחר
        </button>
        <button type="button" onClick={onResend} className="text-brand-700 hover:underline">
          שליחת קוד חדש
        </button>
      </div>
    </AuthShell>
  );
}
